// ColumnsDrawer.tsx
import { memo, useMemo, useState } from "react";
import { Search, X, Columns3, CheckSquare, Square } from "lucide-react";

// UI Components
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { ColumnState } from "./DataTable";

interface ColumnsDrawerProps {
  open: boolean;
  onClose: (open: boolean) => void;
  columns: ColumnState<unknown>[];
  onColumnsChange: (columns: ColumnState<unknown>[]) => void;
}

export const ColumnsDrawer = memo(function ColumnsDrawer({
  open,
  onClose,
  columns,
  onColumnsChange,
}: ColumnsDrawerProps) {
  const [searchTerm, setSearchTerm] = useState("");

  // Filtragem das colunas baseada na busca
  const filteredColumns = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return columns;

    return columns.filter((col) => (col.title || col.key).toLowerCase().includes(term));
  }, [columns, searchTerm]);

  // Toggle Individual
  const toggleColumn = (colId: string, checked: boolean) => {
    onColumnsChange(columns.map((c) => (c.key === colId ? { ...c, isVisible: checked } : c)));
  };

  // Lógica de "Selecionar Tudo" / "Desmarcar Tudo" (Baseado no filtro atual)
  const areAllFilteredSelected = useMemo(() => {
    if (filteredColumns.length === 0) return false;
    return filteredColumns.every((c) => c.isVisible);
  }, [filteredColumns]);

  const toggleAll = () => {
    const newValue = !areAllFilteredSelected;
    const affectedKeys = new Set(filteredColumns.map((c) => c.key));

    onColumnsChange(
      columns.map((c) => (affectedKeys.has(c.key) ? { ...c, isVisible: newValue } : c)),
    );
  };

  // Contagem de colunas visíveis
  const visibleCount = columns.filter((c) => c.isVisible).length;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      {/* Ajuste 1: Removido p-0 e gap-0 para controle manual. Adicionado h-full explícito */}
      <SheetContent
        side="right"
        className="flex h-[100dvh] w-full flex-col border-l p-0 sm:max-w-2xl"
      >
        {/* Header - Fixo */}
        <SheetHeader className="shrink-0 border-b px-6 py-5">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Columns3 className="h-5 w-5 text-primary" />
            Gerenciar Colunas
          </SheetTitle>
          <SheetDescription>
            Mostrando{" "}
            <Badge variant="secondary" className="px-1 py-0">
              {visibleCount}
            </Badge>{" "}
            de {columns.length} colunas disponíveis.
          </SheetDescription>
        </SheetHeader>

        {/* Search & Actions - Fixo (shrink-0 impede que esta área suma no scroll) */}
        <div className="shrink-0 space-y-3 border-b bg-muted/5 px-6 py-4">
          <div className="group relative w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary" />
            <Input
              placeholder="Buscar colunas..."
              className="h-10 bg-background pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {filteredColumns.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleAll}
              className="h-8 w-full justify-start px-2 text-muted-foreground hover:text-foreground"
            >
              {areAllFilteredSelected ? (
                <>
                  <Square className="mr-2 h-4 w-4" />
                  Desmarcar todas listadas
                </>
              ) : (
                <>
                  <CheckSquare className="mr-2 h-4 w-4" />
                  Selecionar todas listadas
                </>
              )}
            </Button>
          )}
        </div>

        {/* Ajuste 2: O ScrollArea agora tem flex-1 e min-h-0 */}
        {/* min-h-0 é o "pulo do gato" em flexbox para permitir que o item encolha e role */}
        <ScrollArea className="min-h-0 w-full flex-1">
          <div className="flex flex-col gap-1 px-6 py-4">
            {filteredColumns.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                <Search className="mb-2 h-8 w-8 opacity-20" />
                <p className="text-sm">Nenhuma coluna encontrada</p>
              </div>
            ) : (
              filteredColumns.map((col) => {
                const isChecked = col.isVisible ?? true;
                return (
                  <div
                    key={col.key}
                    role="button"
                    onClick={() => toggleColumn(col.key, !isChecked)}
                    className={`flex cursor-pointer select-none items-center space-x-3 rounded-md px-3 py-2.5 transition-all ${
                      isChecked ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted"
                    }`}
                  >
                    <Checkbox
                      checked={isChecked}
                      className="pointer-events-none border-muted-foreground/40 data-[state=checked]:border-primary"
                    />
                    <span
                      className={`pointer-events-none flex-1 text-sm font-medium ${
                        isChecked ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {col.title || col.key}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Footer - Fixo no fundo */}
        <SheetFooter className="mt-auto shrink-0 border-t bg-muted/5 p-6">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => onClose(false)}>
            Fechar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
});
